import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  CircularProgress,
  Alert,
  Chip,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  School as SchoolIcon,
  People as PeopleIcon,
  PlayArrow as PlayArrowIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { getMyCourses, getArchivedCourses, TutorCourse } from '../services/tutorCourseService';
import { getRoster } from '../services/tutorRosterService';
import { startSession } from '../services/tutorSessionService';

const TutorDashboard: React.FC = () => {
  const navigate = useNavigate();

  const [activeCourses, setActiveCourses] = useState<TutorCourse[]>([]);
  const [hasArchivedCourses, setHasArchivedCourses] = useState(false);
  const [rosterCounts, setRosterCounts] = useState<Map<number, number>>(new Map());
  const [totalEnrolled, setTotalEnrolled] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [startingSessionCourseId, setStartingSessionCourseId] = useState<number | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [courses, archived] = await Promise.all([getMyCourses(), getArchivedCourses()]);
      setActiveCourses(courses);
      setHasArchivedCourses(archived.length > 0);

      if (courses.length > 0) {
        const rosters = await Promise.all(courses.map((c) => getRoster(c.id)));
        const counts = new Map<number, number>();
        courses.forEach((c, i) => counts.set(c.id, rosters[i].length));
        setRosterCounts(counts);
        setTotalEnrolled(rosters.reduce((sum, r) => sum + r.length, 0));
      }
    } catch {
      setError('Failed to load your dashboard. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleStartSession = async (course: TutorCourse) => {
    if (course.activeSessionId) {
      navigate(`/tutor/courses/${course.id}/session`);
      return;
    }
    setStartingSessionCourseId(course.id);
    try {
      await startSession(course.id);
      navigate(`/tutor/courses/${course.id}/session`);
    } finally {
      setStartingSessionCourseId(null);
    }
  };

  const sortedCourses = [...activeCourses].sort(
    (a, b) => new Date(b.createdAt ?? 0).getTime() - new Date(a.createdAt ?? 0).getTime()
  );
  const topCourses = sortedCourses.slice(0, 3);

  // ── Loading ──────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  // ── Error ────────────────────────────────────────────────────────────────
  if (error) {
    return (
      <Container maxWidth="md" sx={{ mt: 6 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="outlined" onClick={fetchData}>
          Retry
        </Button>
      </Container>
    );
  }

  // ── All courses archived ─────────────────────────────────────────────────
  if (activeCourses.length === 0 && hasArchivedCourses) {
    return (
      <Container maxWidth="md" sx={{ mt: 6 }}>
        <Typography variant="h4" fontWeight={700} gutterBottom>
          Tutor Dashboard
        </Typography>
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              All your courses are archived
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              You have no active courses right now. Create a new course or restore one from your
              archived courses.
            </Typography>
          </CardContent>
          <CardActions sx={{ px: 2, pb: 2, gap: 1 }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate('/tutor/courses')}
            >
              Create New Course
            </Button>
            <Button variant="outlined" onClick={() => navigate('/tutor/courses/archived')}>
              View Archived Courses
            </Button>
          </CardActions>
        </Card>
      </Container>
    );
  }

  // ── Empty / Onboarding ───────────────────────────────────────────────────
  if (activeCourses.length === 0) {
    return (
      <Container maxWidth="md" sx={{ mt: 6 }}>
        <Typography variant="h4" fontWeight={700} gutterBottom>
          Tutor Dashboard
        </Typography>
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="h5" gutterBottom>
              Welcome to StudyStride Tutoring
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              You're all set up as a tutor. Create your first course to start teaching students
              and sharing your expertise.
            </Typography>
            <Button
              variant="contained"
              size="large"
              startIcon={<AddIcon />}
              onClick={() => navigate('/tutor/courses')}
            >
              Create Your First Course
            </Button>
          </CardContent>
          <Divider />
          <CardActions sx={{ px: 2, py: 1.5, gap: 2 }}>
            <Button size="small" onClick={() => navigate('/tutor/courses')}>
              My Courses
            </Button>
            <Button size="small" onClick={() => navigate('/profile-settings')}>
              Profile Settings
            </Button>
          </CardActions>
        </Card>
      </Container>
    );
  }

  // ── Course Summary ───────────────────────────────────────────────────────
  return (
    <Container maxWidth="lg" sx={{ mt: 6, mb: 6 }}>
      <Typography variant="h4" fontWeight={700} gutterBottom>
        Tutor Dashboard
      </Typography>

      {/* Quick actions */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={() => navigate('/tutor/courses')}
        >
          Create New Course
        </Button>
        <Button variant="text" onClick={() => navigate('/tutor/courses')}>
          My Courses
        </Button>
      </Box>

      {/* Stats */}
      <Box sx={{ display: 'flex', gap: 2, mb: 4, flexWrap: 'wrap' }}>
        <Chip
          icon={<SchoolIcon />}
          label={`${activeCourses.length} Active ${activeCourses.length === 1 ? 'Course' : 'Courses'}`}
          color="primary"
          variant="outlined"
          sx={{ fontSize: '0.95rem', px: 1 }}
        />
        <Chip
          icon={<PeopleIcon />}
          label={`${totalEnrolled} ${totalEnrolled === 1 ? 'Student' : 'Students'} Enrolled`}
          color="secondary"
          variant="outlined"
          sx={{ fontSize: '0.95rem', px: 1 }}
        />
      </Box>

      {/* Course cards */}
      <Typography variant="h6" gutterBottom>
        {activeCourses.length > 3 ? 'Recent Courses' : 'My Courses'}
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
          gap: 2,
          mb: 3,
        }}
      >
        {topCourses.map((course) => (
          <Card key={course.id} variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" fontWeight={600} noWrap title={course.name}>
                {course.name}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {rosterCounts.get(course.id) ?? 0}{' '}
                {(rosterCounts.get(course.id) ?? 0) === 1 ? 'student' : 'students'} enrolled
              </Typography>
            </CardContent>
            <CardActions>
              <Button
                size="small"
                onClick={() => navigate(`/tutor/courses/${course.id}/structure`)}
              >
                Manage
              </Button>
              {course.status === 'ACTIVE' && (
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<PlayArrowIcon />}
                  disabled={startingSessionCourseId === course.id}
                  onClick={() => handleStartSession(course)}
                >
                  {course.activeSessionId ? 'Continue Session' : 'Start Session'}
                </Button>
              )}
            </CardActions>
          </Card>
        ))}
      </Box>

      {activeCourses.length > 3 && (
        <Button variant="text" onClick={() => navigate('/tutor/courses')}>
          View All Courses →
        </Button>
      )}
    </Container>
  );
};

export default TutorDashboard;
